'use client'

import React, { Suspense, useEffect, useMemo } from 'react'
import { AppShell, AppShellMain, AppShellNavbar } from '@mantine/core'
import { usePathname } from 'next/navigation'
import { ErrorBoundary } from '../../../shared/ErrorBoundary'
import { Sidebar, SidebarLogo, DEFAULT_SIDEBAR_WIDTH } from './Sidebar'
import { DashboardHeader } from './DashboardHeader'
import { MobileTopHeader, MobileSubHeader } from './MobileHeaders'
import { MobileTabBar } from './MobileTabBar'
import { DashboardNavDrawer } from './DashboardNavDrawer'
import { DashboardNavGroup, findActiveNavItem } from './navigation'

export interface DashboardShellProps {
  /**
   * Stamped as `data-dashboard-theme`, which is what selects the accent block in
   * tokens.css. Scoped here rather than on :root so co-owner's accent cannot
   * leak onto the marketing pages that share its stylesheet.
   */
  theme?: 'landlord' | 'co-owner'
  navGroups: DashboardNavGroup[]
  logo: SidebarLogo
  homeHref: string
  /** Overrides the title derived from the nav config. */
  pageTitle?: string
  profileSlot?: React.ReactNode
  /** Avatar block under the sidebar logo. */
  sidebarProfile?: React.ReactNode
  sidebarFooter?: React.ReactNode
  drawerFooter?: React.ReactNode
  headerActions?: React.ReactNode
  moreTabLabel?: string
  /** Widen for longer nav labels or grouped navigation. Defaults to 210. */
  sidebarWidth?: number
  children: React.ReactNode
}

/**
 * The dashboard chrome, shared by landlord and co-owner.
 *
 * One breakpoint decides everything: 1024px. Above it, a fixed sidebar and a
 * sticky title bar; below it, a top header, a title strip, a bottom tab bar and
 * a full-screen drawer. Mixing two breakpoints is what left the previous
 * co-owner dashboard with a 992–1023px band where the sidebar was hidden but the
 * grid still reserved a column for it — and what left this shell with a
 * 1024–1199px one, when Mantine's `lg` turned out not to be Tailwind's.
 *
 * The content reserves space for the tab bar below that breakpoint, so it never
 * sits over the last row of a table.
 */
export const DashboardShell: React.FC<DashboardShellProps> = ({
  theme = 'landlord',
  navGroups,
  logo,
  homeHref,
  pageTitle,
  profileSlot,
  sidebarProfile,
  sidebarFooter,
  drawerFooter,
  headerActions,
  moreTabLabel,
  sidebarWidth = DEFAULT_SIDEBAR_WIDTH,
  children,
}) => {
  const pathname = usePathname()

  // The accent lives on a data attribute, and several things that need it are
  // not inside this tree: Mantine portals the nav drawer, the tooltips and the
  // filter panels into document.body, and the sidebar sits in AppShellNavbar
  // rather than AppShellMain. Marking the document while the shell is mounted is
  // what reaches all of them. It is removed on unmount, so navigating from the
  // dashboard to a marketing page leaves nothing behind — which is the reason
  // the accent was scoped rather than put on :root in the first place.
  useEffect(() => {
    const root = document.documentElement
    const previous = root.getAttribute('data-dashboard-theme')
    root.setAttribute('data-dashboard-theme', theme)
    return () => {
      if (previous === null) root.removeAttribute('data-dashboard-theme')
      else root.setAttribute('data-dashboard-theme', previous)
    }
  }, [theme])

  // One source for the title: whichever nav item owns the current path. The
  // previous dashboards each kept a hand-written path-to-title map that had to
  // be edited every time a route was added, and silently fell back to "Dashboard"
  // when it wasn't.
  const title = useMemo(
    () => pageTitle ?? findActiveNavItem(pathname, navGroups)?.label ?? '',
    [pageTitle, pathname, navGroups],
  )

  // More destinations than tabs means the overflow tab has to be there.
  const primaryCount = navGroups.reduce(
    (total, group) => total + group.items.filter((item) => item.mobilePrimary).length,
    0,
  )
  const totalCount = navGroups.reduce((total, group) => total + group.items.length, 0)

  return (
    <AppShell
      layout="alt"
      // '64em', not 'lg'. Mantine's lg is 75em/1200px while Tailwind's is
      // 64rem/1024px, and every other responsive decision in this shell is a
      // Tailwind class. Left as 'lg' the two disagreed between 1024 and 1199px:
      // Mantine collapsed the sidebar while Tailwind had already switched to
      // desktop and hidden the mobile chrome, so that band had no navigation at
      // all. This is the same class of dead zone the co-owner rewrite set out to
      // remove, so it is spelled out rather than named.
      navbar={{ width: sidebarWidth, breakpoint: '64em', collapsed: { mobile: true } }}
      styles={{
        main: {
          background:
            'linear-gradient(180deg, var(--dashboard-canvas) 0%, var(--dashboard-canvas-tint) 100%)',
          backgroundAttachment: 'fixed',
          minHeight: '100vh',
        },
      }}
    >
      <AppShellNavbar>
        <Sidebar
          navGroups={navGroups}
          logo={logo}
          homeHref={homeHref}
          profile={sidebarProfile}
          footer={sidebarFooter}
          width={sidebarWidth}
        />
      </AppShellNavbar>

      <div className="lg:hidden">
        <DashboardNavDrawer navGroups={navGroups} footer={drawerFooter} />
      </div>

      <AppShellMain data-dashboard-theme={theme}>
        <div className="hidden lg:block bg-white/80 backdrop-blur-sm sticky top-0 z-50">
          <DashboardHeader title={title} profileSlot={profileSlot} actions={headerActions} />
        </div>

        <div className="lg:hidden bg-white">
          <MobileTopHeader logo={logo} homeHref={homeHref} profileSlot={profileSlot} />
          <MobileSubHeader title={title} />
        </div>

        {/* pb-24 below lg clears the fixed tab bar, so the last row of a table
            is never sitting under it. */}
        {/* font-poppins once, here, rather than on every element of every
            screen. The kit components still set it themselves so they keep
            their type when used outside this shell. */}
        <div className="font-poppins px-4 lg:px-7 pt-4 lg:pt-6 pb-24 lg:pb-8 max-w-[2000px] mx-auto w-full">
          <ErrorBoundary>{children}</ErrorBoundary>
        </div>
      </AppShellMain>

      {/* Outside AppShellFooter on purpose. Mantine's footer height takes named
          breakpoint keys only, which would drag Mantine's 1200px `lg` back into
          a layout that is otherwise decided at Tailwind's 1024px. A fixed
          element under a Tailwind class keeps one breakpoint for the whole
          shell. */}
      <div className="lg:hidden fixed inset-x-0 bottom-0 z-[200]" data-dashboard-theme={theme}>
        <MobileTabBar
          navGroups={navGroups}
          showMore={totalCount > primaryCount}
          moreLabel={moreTabLabel}
        />
      </div>
    </AppShell>
  )
}

/** Wraps the shell in Suspense, which useSearchParams inside it requires. */
export const DashboardShellSuspense: React.FC<DashboardShellProps> = (props) => (
  <Suspense>
    <DashboardShell {...props} />
  </Suspense>
)
